import { LitElement, css, html, unsafeCSS, nothing } from 'lit';
import { live } from 'lit/directives/live.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { createInputMask } from '@/directives/InputMaskDirective.js';
import styles from './style.scss?inline';
import { ATTRIBUTES } from '@/components/input/constants.js';
import { ATTRIBUTES as INPUT_COLOR_ATTRIBUTES } from './constants.js';
import { getMessage } from '@/localization.js';
import messages from './localization.json';

/**
 * @event change
 * @event input
 *
 * @csspart container
 * @csspart button
 * @csspart outline
 * @csspart color
 * @csspart input
 * @csspart helper-text
 */
export default class SotbitInputColor extends LitElement {
    static formAssociated = true;

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        value: { type: String, reflect: true },
        helperText: { type: String, attribute: ATTRIBUTES.HELPER_TEXT },
    };

    #COLOR_REGEX = /^#[0-9a-f]{6}$/i;
    #DEFAULT_COLOR = '#000000';

    constructor() {
        super();

        this._internals = this.attachInternals();
        this._colorRef = createRef();
        this._inputTextRef = createRef();
        this._inputColorRef = createRef();

        this.value = this.#DEFAULT_COLOR;

        this._lastValidColor = this.value;
    }

    get value() {
        return this._value;
    }

    set value(newValue) {
        this._setColor(newValue);
        this._updateInputTextValue(this.value);

        this._lastValidColor = this._value;
    }

    get name() {
        return this.getAttribute('name') ?? '';
    }

    showColorPicker() {
        if (this.getAttribute(INPUT_COLOR_ATTRIBUTES.DISABLED) !== null) {
            return;
        }

        this._inputColorRef.value?.click();

        this.setAttribute(INPUT_COLOR_ATTRIBUTES.ACTIVE, '');

        setTimeout(() => {
            document.addEventListener(
                'click',
                () => {
                    this.removeAttribute(INPUT_COLOR_ATTRIBUTES.ACTIVE);
                },
                { once: true }
            );
        });
    }

    firstUpdated() {
        this._setCssColorProperty(this.value);
        this._updateInputTextValue(this.value);
    }

    formAssociatedCallback() {
        this._internals?.setFormValue(this.value);
    }

    formDisabledCallback(disabled) {
        this.toggleAttribute(INPUT_COLOR_ATTRIBUTES.DISABLED, disabled);
    }

    formResetCallback() {
        this.value = this.#DEFAULT_COLOR;
    }

    formStateRestoreCallback(state) {
        this.value = state;
    }

    _updateInputTextValue(value) {
        if (!(this._inputTextRef.value instanceof HTMLInputElement)) {
            return;
        }

        this._inputTextRef.value.value = value;

        this._mask?.updateValue();
    }

    _handleInputColorChange() {
        this.removeAttribute(INPUT_COLOR_ATTRIBUTES.ACTIVE);

        this.dispatchEvent(
            new Event('change', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleInputColorInput(event) {
        this.value = event.target.value;

        this.dispatchEvent(
            new Event('input', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleInputTextBlur() {
        if (this._lastValidColor !== this.value) {
            this.dispatchEvent(
                new Event('change', {
                    bubbles: true,
                    composed: true,
                })
            );

            this._lastValidColor = this.value;
        } else {
            this.value = this._lastValidColor;
        }
    }

    _handleInputTextInput(event) {
        this._setColor(event.target.value);

        this.dispatchEvent(
            new Event('input', {
                bubbles: true,
                composed: true,
            })
        );
    }

    _setCssColorProperty(value) {
        this._colorRef.value?.style.setProperty('--color', value);
    }

    _isValidColor(color) {
        return this.#COLOR_REGEX.test(color);
    }

    _handleInputRefUpdate(input) {
        if (this._inputTextRef.value || !(input instanceof HTMLInputElement)) {
            return;
        }

        this._mask = createInputMask(input, {
            mask: '{#}HEX',
            lazy: false,
            blocks: {
                HEX: {
                    mask: '******',
                    lazy: true,
                    definitions: {
                        '*': /[0-9a-f]/i,
                    },
                    prepareChar: (char) => char.toUpperCase(),
                },
            },
        });

        this._inputTextRef.value = input;
    }

    /**
     * Sets color without changing text input value
     */
    _setColor(color) {
        if (!this._isValidColor(color)) {
            color = this._lastValidColor;

            this._internals.setValidity({ badInput: true }, getMessage(messages, 'badColor'));
        } else {
            this._internals.setValidity({});
        }

        this._value = color.toUpperCase();

        this._internals?.setFormValue(this._value);
        this._setCssColorProperty(this._value);
    }

    render() {
        return html`<div part="container">
                <div part="button" @click="${this.showColorPicker}">
                    <div part="outline">
                        <input
                            type="color"
                            .value="${live(this.value)}"
                            @change="${this._handleInputColorChange}"
                            @input="${this._handleInputColorInput}"
                            ${ref(this._inputColorRef)}
                        />
                        <div part="color" ${ref(this._colorRef)}></div>
                    </div>
                </div>
                <input
                    part="input"
                    type="text"
                    maxlength="7"
                    @blur="${this._handleInputTextBlur}"
                    @input="${this._handleInputTextInput}"
                    @paste="${this._handleInputTextInput}"
                    ${ref(this._handleInputRefUpdate)}
                />
            </div>
            <div part="helper-text">${this.helperText || nothing}</div>`;
    }
}
